// Admin-side catalog for the notification rule builder. The admin app cannot
// import from backend/supabase/functions, so keep this in sync with
// _shared/eventCatalog.ts (events) + _shared/ruleEngine.ts (operators).
//
// `wired` MEANS AN EMITTER FIRES THIS EVENT IN PRODUCTION — not that emitter code
// exists in the repo. That distinction is load-bearing and this file got it wrong
// for eleven weeks. 20260623130000_notification_emitters.sql creates DB-webhook
// triggers for photo.tagged and devotional.published, enables their rules, and
// this catalog was updated to match — on the strength of the migration having
// run, not of a trigger existing. It never installed them: its first guard is
// `to_regproc('supabase_functions.http_request') is null`, which is TRUE on this
// project, so the whole DO block returns with a NOTICE and creates nothing.
//
// Verified against prod 2026-09-10, three independent ways:
//   • photo_tag_nominations and devotionals carry exactly ONE trigger each, and
//     it is `admin_audit`. Dashboard → Database → Webhooks builds the same
//     supabase_functions trigger, so that route cannot have been used either.
//   • RULE_ENGINE_WEBHOOK_SECRET is absent from the project's function secrets,
//     so feed-activity would reject the POST even if a trigger made one.
//   • There is no shortage of input: 20 devotionals, all published after that
//     migration, and 20 tag nominations, every one of them a tag of ANOTHER
//     member (what the trigger's WHEN clause selects for). Rules …006 and …008
//     have produced zero notifications; the app-emitted rsvp and mentorship
//     rules produced 20 over the same period.
//
// UPDATE 2026-09-22 — there was a THIRD latch, and photo.tagged is now repaired.
//
// The advice above ("install the two webhooks, or port them to net.http_post")
// was half wrong: installing the webhook by hand could not have worked either.
// The header 20260623130000 builds carries only x-webhook-secret and NO
// Authorization, while feed-activity had no [functions.feed-activity] entry in
// config.toml and so ran at the default verify_jwt = true — the API gateway
// would have 401'd the POST before the function ran. stream-feed-webhook, the
// one inbound caller that works, sends BOTH headers for exactly this reason.
//
// photo.tagged is repaired by 20260922120000_photo_tag_emitter.sql (a pg_net
// trigger + the locked-down public.rule_engine_config table) together with
// [functions.feed-activity] verify_jwt = false.
//
// devotional.published is deliberately NOT repaired: members already receive
// that copy from the nightly devotional_streak_at_risk_run() cron, so wiring its
// webhook too would double the notification. Inert is the correct state for it.
//
// photo.tagged nevertheless STAYS wired: false here, because `wired` means it
// fires IN PRODUCTION and two prerequisites are operator actions that no merge
// can assert. Flip it only after observing BOTH, against prod:
//   1. select public.rule_engine_config_is_configured();  -- must be true, i.e.
//      the secret is set AND equals the project's RULE_ENGINE_WEBHOOK_SECRET
//   2. a real tag produced a row:
//      select count(*) from notifications
//       where dedup_key like '00000000-0000-4000-8000-000000000006:%';
// __tests__/catalog.test.ts pins the flag by name so flipping it fails loudly
// until someone records what they actually checked. That tripwire is the point.
//
// reaction.created is emitted by stream-feed-webhook (deployed, ACTIVE); its
// trigger is registered in the Stream dashboard and so is not checkable from
// this repo — left as wired, on the same footing it has always had. leaderboard
// and birthday have no backing data, and chat would duplicate GetStream's own
// message notifications.

export interface EventOption {
  value: string;
  label: string;
  fields: string[];
  placeholders: string[];
  // Whether an emitter fires this event IN PRODUCTION today — see the header for
  // why that is the test, and for how two of these were badged true on the
  // strength of a migration that silently installed nothing. A rule on an
  // unwired event stays inert (won't fire) even when enabled, so it is authorable
  // ahead of its emitter on purpose; the builder surfaces the flag so operators
  // aren't left explaining silence.
  wired: boolean;
}

export const EVENT_OPTIONS: EventOption[] = [
  { value: 'donation.created', label: 'Donation made', fields: ['amount', 'fund', 'frequency', 'status'], placeholders: ['{who}', '{class_year}', '{amount}', '{fund}'], wired: true },
  { value: 'rsvp.created', label: 'RSVP submitted', fields: ['event_id', 'guests', 'meal', 'status'], placeholders: ['{who}', '{class_year}', '{event_title}', '{guests}'], wired: true },
  { value: 'member.joined', label: 'New member joined', fields: ['class_year'], placeholders: ['{who}', '{class_year}'], wired: true },
  { value: 'mentorship_request.created', label: 'Mentorship request', fields: ['to_user_id', 'message', 'status'], placeholders: ['{who}', '{class_year}'], wired: true },
  { value: 'mentorship_request.accepted', label: 'Mentorship request accepted', fields: ['from_user_id', 'to_user_id', 'message', 'status'], placeholders: ['{who}', '{class_year}'], wired: true },
  { value: 'mentorship_request.declined', label: 'Mentorship request declined', fields: ['from_user_id', 'to_user_id', 'message', 'status'], placeholders: ['{who}', '{class_year}'], wired: true },
  { value: 'mentorship_request.deferred', label: 'Mentorship request deferred', fields: ['from_user_id', 'to_user_id', 'message', 'status'], placeholders: ['{who}', '{class_year}'], wired: true },
  { value: 'job.created', label: 'Job posted', fields: ['role', 'org', 'loc'], placeholders: ['{who}', '{role}', '{org}', '{loc}'], wired: true },
  // ── Emitted outside the app: a Stream webhook, a DB webhook, or a job (see header) ──
  // activity_id + others_phrase (feed engagement push): the Comments tap target and
  // the pure "and N others" suffix that replaced the dead "& 0 others" copy — see
  // 20261014120000_reaction_notification_route.sql and eventCatalog.ts's
  // reaction.created comment.
  { value: 'reaction.created', label: 'Feed reaction received (Stream)', fields: ['reactor', 'others_count', 'others_phrase', 'activity_label', 'activity_id', 'activity_preview'], placeholders: ['{reactor}', '{others_count}', '{others_phrase}', '{activity_label}', '{activity_id}', '{activity_preview}'], wired: true },
  // comment.created — same Stream webhook as reaction.created just above
  // (stream-feed-webhook), but wired: false: no Stream `feeds` webhook is
  // scoped to comment events in the dashboard, so this rule
  // (00000000-0000-4000-8000-000000000014, 20261014120200_comment_notification_rule.sql)
  // is authored and enabled but cannot fire. Flip only after an operator
  // observes BOTH: (1) the Stream dashboard's feeds webhook event_hooks
  // includes a comment-added event, and (2) a real comment produced a
  // notifications row (select count(*) from notifications where dedup_key
  // like '00000000-0000-4000-8000-000000000014:%') — same two-part
  // verification discipline as photo.tagged's entry above.
  { value: 'comment.created', label: 'Feed comment received (Stream)', fields: ['commenter', 'preview', 'activity_label', 'activity_id'], placeholders: ['{commenter}', '{preview}', '{activity_label}', '{activity_id}'], wired: false },
  // {photos_word} is the plural of {photo_count} ("photo"/"photos") — the title reads
  // "{photo_count} new {photos_word}" so it cannot say "1 new photos", and count == 1 is
  // the COMMON case (see _shared/photoTagNotify.ts). {photo_id} is the deep-link input:
  // a route of {"screen":"ImageLightbox","params":{"photoId":"{photo_id}"}} opens the photo.
  { value: 'photo.tagged', label: 'Tagged in photos', fields: ['photo_count', 'photos_word', 'tagger', 'album_title', 'photo_id'], placeholders: ['{photo_count}', '{photos_word}', '{tagger}', '{album_title}', '{photo_id}'], wired: false },
  { value: 'leaderboard.rank_changed', label: 'Class rank changed', fields: ['direction', 'rank', 'gap', 'ahead_year'], placeholders: ['{class_year}', '{rank}', '{gap}', '{ahead_year}'], wired: false },
  { value: 'devotional.published', label: 'New devotional published', fields: [], placeholders: [], wired: false },
  { value: 'birthday.today', label: 'Member birthday today', fields: [], placeholders: ['{who}', '{class_year}'], wired: false },
  { value: 'chat_message.created', label: 'New chat replies (Stream)', fields: ['thread_title', 'sender', 'preview', 'new_count'], placeholders: ['{thread_title}', '{sender}', '{preview}', '{new_count}'], wired: false },
  // ── Jaguar-Owned — dark behind feature flags until launch (wired stays false until a flag is on in production) ──
  { value: 'business.submitted', label: 'Business listing submitted', fields: ['name', 'category_slug', 'business_type', 'status'], placeholders: ['{who}', '{name}'], wired: false },
  { value: 'business.approved', label: 'Business listing approved', fields: ['owner_user_id', 'name', 'status', 'decision_note'], placeholders: ['{name}'], wired: false },
  { value: 'business.changes_requested', label: 'Business listing needs changes', fields: ['owner_user_id', 'name', 'status', 'decision_note'], placeholders: ['{name}'], wired: false },
  { value: 'business.rejected', label: 'Business listing not approved', fields: ['owner_user_id', 'name', 'status', 'decision_note'], placeholders: ['{name}'], wired: false },
  { value: 'business.removed', label: 'Business listing removed', fields: ['owner_user_id', 'name', 'status', 'decision_note'], placeholders: ['{name}'], wired: false },
  { value: 'business_vouch.created', label: 'Business vouched for', fields: ['owner_user_id', 'business_id', 'relationship'], placeholders: ['{who}'], wired: false },
  { value: 'business_note.created', label: 'Private note to a business owner', fields: ['owner_user_id', 'business_id'], placeholders: ['{who}'], wired: false },
  { value: 'business_request.created', label: 'Ask the Jaguars request', fields: ['routed_owner_user_ids', 'category_slug', 'area', 'is_private'], placeholders: [], wired: false },
  { value: 'business_reply.created', label: 'Ask the Jaguars reply', fields: ['request_id', 'business_id', 'kind'], placeholders: ['{who}'], wired: false },
  { value: 'business_request.outcome_recorded', label: 'Ask the Jaguars outcome', fields: ['outcome', 'chosen_owner_user_id', 'recommended_by_user_id', 'is_private'], placeholders: ['{who}'], wired: false },
];

// Convenience: is a given trigger_event backed by an emitter today?
export function isEventWired(value: string | undefined): boolean {
  return EVENT_OPTIONS.find((e) => e.value === value)?.wired ?? true;
}

// Trigger-event dropdown split into two groups so operators can see at a glance
// which events actually fire. Unwired events keep a "· not wired" suffix as a
// second, in-row cue. Both groups stay selectable — unwired rules are authored
// ahead of their emitter on purpose.
export const EVENT_OPTIONS_GROUPED = [
  {
    label: 'Fires today',
    options: EVENT_OPTIONS.filter((e) => e.wired).map((e) => ({ value: e.value, label: e.label })),
  },
  {
    label: "Not wired (won't fire yet)",
    options: EVENT_OPTIONS.filter((e) => !e.wired).map((e) => ({
      value: e.value,
      label: `${e.label} · not wired`,
    })),
  },
];

export const OPERATOR_OPTIONS = [
  { value: 'eq', label: '= equals' },
  { value: 'neq', label: '≠ not equals' },
  { value: 'gt', label: '> greater than' },
  { value: 'gte', label: '≥ at least' },
  { value: 'lt', label: '< less than' },
  { value: 'lte', label: '≤ at most' },
  { value: 'in', label: 'in (comma list)' },
  { value: 'contains', label: 'contains' },
];

export const KIND_OPTIONS = ['gift', 'rsvp', 'member', 'photo', 'news', 'milestone'].map((k) => ({ value: k, label: k }));
export const GRP_OPTIONS = ['Today', 'This Week', 'Earlier'].map((g) => ({ value: g, label: g }));
export const RECIPIENT_OPTIONS = [
  { value: 'actor', label: 'The member who did it' },
  { value: 'members', label: 'Specific members' },
  { value: 'same_as_audience', label: 'Same as feed audience' },
  { value: 'all_members', label: 'All members' },
  { value: 'class_year', label: 'A class year' },
  { value: 'admins', label: 'Admins' },
  { value: 'recipient_field', label: 'User in a row field' },
];
export const DEDUP_OPTIONS = [
  { value: 'per_row', label: 'Once per row' },
  { value: 'per_user_row', label: 'Once per user + row' },
  { value: 'always', label: 'Always (no dedup)' },
];

// Activity audience: who a feed item is tied to. Stored on
// activity_template.audience.type. 'all_members' is labeled "Everyone".
export const AUDIENCE_OPTIONS = [
  { value: 'actor', label: 'The member who did it' },
  { value: 'members', label: 'Specific members' },
  { value: 'class_year', label: 'A class year' },
  { value: 'all_members', label: 'Everyone' },
  { value: 'generic', label: 'Generic (no member)' },
];
